import { ordinalSuffix, getPlayerLink, formatNumberWithDecimal } from './shared_helpers'
import { env_url } from './endpoints'

export const NFLsilhouette = `${env_url}/player-silhouette-hockey-cut.png`

export const imgErrNFL = ev => {
    ev.target.src = NFLsilhouette
}

export const nflTeamNameIDArrayMap = new Map([
    [323, 'atlanta-falcons'],
    [324, 'buffalo-bills'],
    [325, 'houston-texans'],
    [326, 'chicago-bears'],
    [327, 'cincinnati-bengals'],
    [329, 'cleveland-browns'],
    [331, 'dallas-cowboys'],
    [332, 'denver-broncos'],
    [334, 'detroit-lions'],
    [335, 'green-bay-packers'],
    [336, 'tennessee-titans'],
    [338, 'indianapolis-colts'],
    [339, 'kansas-city-chiefs'],
    [341, 'las-vegas-raiders'],
    [343, 'los-angeles-rams'],
    [345, 'miami-dolphins'],
    [347, 'minnesota-vikings'],
    [348, 'new-england-patriots'],
    [350, 'new-orleans-saints'],
    [351, 'new-york-giants'],
    [352, 'new-york-jets'],
    [354, 'philadelphia-eagles'],
    [355, 'arizona-cardinals'],
    [356, 'pittsburgh-steelers'],
    [357, 'los-angeles-chargers'],
    [359, 'san-francisco-49ers'],
    [361, 'seattle-seahawks'],
    [362, 'tampa-bay-buccaneers'],
    [363, 'washington-commanders'],
    [364, 'carolina-panthers'],
    [365, 'jacksonville-jaguars'],
    [366, 'baltimore-ravens'],
])

export const labelQuarters = (quarters = []) => {
    let quartersCopy = quarters.slice(0)

    quartersCopy.forEach((quarter, i) => {
        if (i < 4) {
            quarter.label = i + 1
        } else if (i === 4) {
            quarter.label = 'OT'
        } else {
            quarter.label = i - 3 + 'OT'
        }
    })

    return quartersCopy
}

export const labelQuartersFullName = (quarters = []) => {
    let quartersCopy = quarters.slice(0)

    quartersCopy.forEach((quarter, i) => {
        if (i < 4) {
            quarter.label = ordinalSuffix(i + 1) + ' Quarter'
        } else if (i === 4) {
            quarter.label = 'OT'
        } else {
            quarter.label = i - 3 + 'OT'
        }
    })

    return quartersCopy
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Passing Yards',
            categoryKey: 'passing_leader',
            valueKey: 'yards',
        },
        {
            title: 'Rushing Yards',
            categoryKey: 'rushing_leader',
            valueKey: 'rushing_yards', // check if there in a rushing_yards value to handle cases where the QB is a passing leader as well as a rushing leader.
            alternateValueKey: 'yards',
        },
        {
            title: 'Receiving Yards',
            categoryKey: 'receiving_leader',
            valueKey: 'yards',
        },
        {
            title: 'Field Goals Made',
            categoryKey: 'field_goals_made_leader',
            valueKey: 'field_goals_made',
        },
        {
            title: 'Tackles',
            categoryKey: 'tackles_leader',
            valueKey: 'tackles',
        },
        {
            title: 'Sacks',
            categoryKey: 'sacks_leader',
            valueKey: 'sacks',
            decimalPlaces: 1,
        },
        {
            title: 'Interceptions',
            categoryKey: 'interceptions_leader',
            valueKey: 'interceptions',
        },
        {
            title: 'Forced Fumbles',
            categoryKey: 'forced_fumbles_leader',
            valueKey: 'forced_fumbles',
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.alternateValueKey) {
            homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey]
            visitingTeamValue =
                visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey]
        }

        if (leaderCategory.decimalPlaces) {
            homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces)
            visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamPlayerImage: homeTeamData.image_urls.xs,
            visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
    if (!visitingTeam.season_stats || !homeTeam.season_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Passing Yards Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'passing_yards_per_game',
        },
        {
            title: 'Rushing Yards Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'rushing_yards_per_game',
        },
        {
            title: 'Points Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'points_scored_per_game',
        },
        {
            title: 'Points Allowed Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'points_allowed_per_game',
        },
        { title: 'Touchdowns', valueKey: 'touchdowns' },
        {
            title: 'Scoring Efficiency',
            valueKey: 'scoring_efficiency_percentage',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}%`,
        },
        {
            title: 'Third Down Efficiency',
            valueKey: 'third_down_efficiency_percentage',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}%`,
        },
        {
            title: '300 Yard Passing Games',
            valueKey: 'three_hundred_yard_games_passing',
        },
        {
            title: '100 Yard Rushing Games',
            valueKey: 'hundred_yard_games_rushing',
        },
        {
            title: '100 Yard Receiving Games',
            valueKey: 'hundred_yard_games_receiving',
        },
        {
            title: 'Takeaways',
            valueKey: 'takeaways',
        },

        { title: 'Interceptions', valueKey: 'interceptions' },
        { title: 'Sacks', valueKey: 'sacks' },
    ]

    const teamStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.season_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey]

        if (!homeTeamValue || !visitingTeamValue) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return teamStats.filter(n => n) // filter out null values
}
export function formatGameStats(homeTeam, visitingTeam) {
    if (!visitingTeam.game_stats || !homeTeam.game_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Total Yards',
            valueKey: 'total_yards',
        },
        {
            title: 'Passing Yards',
            valueKey: 'passing_yards',
        },
        {
            title: 'Rushing Yards',
            valueKey: 'rushing_yards',
        },
        {
            title: 'Total Plays',
            valueKey: 'game_total_plays',
        },
        {
            title: 'Avg. Yards per Play',
            valueKey: 'game_total_average',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
        },
        {
            title: 'First Downs',
            valueKey: 'first_downs',
        },
        {
            title: 'Third Down Efficiency',
            valueKey: 'third_down_efficiency_percentage',
            valueFormatter: n => `${n}%`,
        },
        {
            title: 'Penalty Yards',
            valueKey: 'penalty_yards',
        },
        { title: 'Turnovers', valueKey: 'turnovers' },
        { title: 'Tackles', valueKey: 'tackles' },
        {
            title: 'QB Sacks',
            valueKey: 'sacks',
        },
        {
            title: 'Sack Yards Lost',
            valueKey: 'yards_lost',
        },
        { title: 'Time of Possession', valueKey: 'possession_time' },
    ]

    const gameStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.game_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey]

        if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
            homeTeamValue = parseFloat(homeTeamValue) || 0
        }
        if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
            visitingTeamValue = parseFloat(visitingTeamValue) || 0
        }

        if (statCategory.valueKey === 'possession_time') {
            homeTeamValue = `${homeTeam.game_stats.possession_minutes}:${homeTeam.game_stats.possession_seconds}`
            visitingTeamValue = `${visitingTeam.game_stats.possession_minutes}:${visitingTeam.game_stats.possession_seconds}`
        }

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return gameStats.filter(n => n) // filter out null values
}

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = formatTeamYardsInText(play.description)
        const teamLogo =
            play.possession === homeTeam.short_name
                ? homeTeam.image_url_90
                : visitingTeam.image_url_90

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.play_type_id),
            bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
            teamLogo: teamLogo,
        }
    })

    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

export function formatPassingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'COMP', dataKey: 'passes_completed', bold: false, textAlign: 'center' },
            { title: 'ATT', dataKey: 'passes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'COMP', dataKey: 'passes_completed', bold: false, textAlign: 'center' },
            { title: 'ATT', dataKey: 'passes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerPassesData => {
                return {
                    ...playerPassesData,
                    player_name: getPlayerLink(playerPassesData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
        homeTeamBoxscoreStats
            .map(playerPassesData => {
                return {
                    ...playerPassesData,
                    player_name: getPlayerLink(playerPassesData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatRushingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'ATT', dataKey: 'rushes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_rush', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'ATT', dataKey: 'rushes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_rush', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatReceivingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'TGT', dataKey: 'targets', bold: false, textAlign: 'center' },
            { title: 'REC', dataKey: 'receptions', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_receiving', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'TGT', dataKey: 'targets', bold: false, textAlign: 'center' },
            { title: 'REC', dataKey: 'receptions', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_receiving', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatKickingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'FG', dataKey: 'made', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick', bold: false, textAlign: 'center' },
            { title: 'XP', dataKey: 'extra_points', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'FG', dataKey: 'made', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick', bold: false, textAlign: 'center' },
            { title: 'XP', dataKey: 'extra_points', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                    extra_points: formatExtraPoints(playerData),
                }
            })
            .sort((a, b) => b.made - a.made),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                    extra_points: formatExtraPoints(playerData),
                }
            })
            .sort((a, b) => b.made - a.made),
    ]

    return { columns, data }
}

export function formatPuntingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'P', dataKey: 'punts', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'NET AVG', dataKey: 'net_average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'P', dataKey: 'punts', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'NET AVG', dataKey: 'net_average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatKickReturnsBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick_return', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick_return', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatPuntReturnsBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'PR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt_return', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt_return', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatDefenseBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'TCKL', dataKey: 'tackles', bold: false, textAlign: 'center' },
            { title: 'SACK', dataKey: 'sacks', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
            { title: 'PDEF', dataKey: 'passes_defensed', bold: false, textAlign: 'center' },
            { title: 'FF', dataKey: 'forced_fumbles', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'TCKL', dataKey: 'tackles', bold: false, textAlign: 'center' },
            { title: 'SACK', dataKey: 'sacks', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
            { title: 'PDEF', dataKey: 'forced_fumbles', bold: false, textAlign: 'center' },
            { title: 'FF', dataKey: 'forced_fumbles', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.tackles - a.tackles),
        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: getPlayerLink(playerData),
                }
            })
            .sort((a, b) => b.tackles - a.tackles),
    ]

    return { columns, data }
}

export function formatInjuries(teamInjuries = []) {
    const formattedInjuries = teamInjuries.map(injury => {
        return {
            playerName: getPlayerLink(injury),
            playerPosition: injury.short_position,
            status: `${injury.status.charAt(0).toUpperCase()}${injury.status.slice(1)}`,
            disabledListStatus: injury.disability_list_status,
            timestamp: injury.timestamp,
            emptyColumn: ' ',
        }
    })

    return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp)
}

export function formatGameAlert(down, distance, ballLocation) {
    if (!down || !distance || !ballLocation) {
        return null
    } else {
        return `${ordinalSuffix(down)} and ${distance} on ${ballLocation.toUpperCase()}`
    }
}

function formatExtraPoints(playerData) {
    if (playerData.extra_points_made && playerData.extra_points_attempted) {
        return `${playerData.extra_points_made}/${playerData.extra_points_attempted}`
    } else {
        return null
    }
}

function setPlayTitle(play) {
    const yardlineTeamRegex = play.yardline.match(/[a-zA-Z]+/g) || []
    const yardLineDistanceRegex = play.yardline.match(/[0-9]+/g)
    const yardLineTeam = (yardlineTeamRegex[0] || '').toUpperCase()
    const yardLineDistance = yardLineDistanceRegex[0] || ''

    const playTypeText = play.play_type || ''

    switch (play.play_type_id) {
        case 5:
            return 'KICKOFF'
        case 6:
            return 'KICKOFF RETURN'
        case 47:
            return 'BLOCKED EXTRA POINT'
        case 52:
            return 'TWO POINT CONVERSION'
        case 53:
        case 54:
        case 56:
            return 'MISSED TWO POINT CONVERSION'
        case 57:
        case 58:
            return 'TIMEOUT'
        case 41:
            return 'ONSIDE KICKOFF'
        case 22:
        case 55:
            return playTypeText.toUpperCase()
        default:
            return `${ordinalSuffix(play.down).toUpperCase()} & ${
                play.distance
            } / ${yardLineTeam} ${yardLineDistance}`
    }
}

function shortenPlayerNames(originalText, players = []) {
    let newText = originalText

    if (!players.length) {
        return originalText
    }

    for (const player of players) {
        const textToReplace = `${player.first_name} ${player.last_name}`
        const playerInitial = player.first_name.charAt(0)
        const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`

        newText = newText.replace(textToReplace, replacementText)
    }
    return newText
}

function formatTeamYardsInText(originalText = '') {
    const teamYardageTexts = originalText.match(/[a-zA-Z]+[0-9]+/gm) || []
    if (!teamYardageTexts.length) {
        return originalText
    }

    teamYardageTexts.forEach(teamYardage => {
        const teamTricode = (teamYardage.match(/[a-zA-Z]+/g)[0] || '').toUpperCase()
        const teamYards = teamYardage.match(/[0-9]+/g)[0] || ''
        originalText = originalText.replace(teamYardage, `${teamTricode} ${teamYards}`)
    })

    return originalText
}

function getPlayColor(playTypeId) {
    const darkBlue = '#0B223E'
    const red = '#DA291C'
    const grey = '#636A71'
    const brightBlue = '#0D5EB8'

    const playColorMap = new Map([
        [1, darkBlue],
        [2, darkBlue],
        [3, brightBlue],
        [4, darkBlue],
        [5, grey],
        [6, darkBlue],
        [7, grey],
        [8, darkBlue],
        [9, brightBlue],
        [10, red],
        [11, red],
        [12, ''],
        [13, ''],
        [14, brightBlue],
        [15, ''],
        [16, ''],
        [17, darkBlue],
        [18, darkBlue],
        [19, ''],
        [20, darkBlue],
        [21, ''],
        [22, darkBlue],
        [23, ''],
        [24, ''],
        [25, ''],
        [26, ''],
        [27, ''],
        [28, ''],
        [29, ''],
        [30, ''],
        [31, ''],
        [32, ''],
        [33, ''],
        [34, ''],
        [35, darkBlue],
        [36, ''],
        [37, ''],
        [38, ''],
        [39, ''],
        [40, ''],
        [41, grey],
        [42, darkBlue],
        [43, ''],
        [44, ''],
        [45, ''],
        [46, ''],
        [47, darkBlue],
        [48, ''],
        [49, ''],
        [50, ''],
        [51, ''],
        [52, darkBlue],
        [53, darkBlue],
        [54, darkBlue],
        [55, darkBlue],
        [56, darkBlue],
        [57, grey],
        [58, grey],
    ])

    return playColorMap.get(playTypeId) || grey
}

export function formatSeasonRecord(team) {
    if (team?.season_stats?.wins && team?.season_stats?.losses) {
        return `${team.season_stats.wins}-${team.season_stats.losses}`
    } else {
        return null
    }
}

export function formatTeamRecord(team) {
    const teamDivision = team.division || {}

    if (teamDivision.rank && teamDivision.short_name) {
        return `${ordinalSuffix(teamDivision.rank)}, ${teamDivision.short_name}`
    } else {
        return null
    }
}
